import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Button,
  MobileStepper,
  Container,
  useMediaQuery
} from '@mui/material';
import SwipeableViews from 'react-swipeable-views';
import { autoPlay } from 'react-swipeable-views-utils';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import FavoriteIcon from '@mui/icons-material/Favorite';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useRole, ROLES } from '../../contexts/RoleContext';

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

const onboardingSteps = [
  {
    label: 'Smart Property Search',
    description: 'Find the perfect foreclosure properties using our advanced search filters. Filter by price, location, property type, and more.',
    icon: <SearchIcon sx={{ fontSize: 80, color: '#1B5E20' }} />,
    color: '#e8f5e9'
  },
  {
    label: 'Market Analytics',
    description: 'Get detailed market insights and property analytics to make informed investment decisions.',
    icon: <AnalyticsIcon sx={{ fontSize: 80, color: '#1B5E20' }} />,
    color: '#f1f8e9'
  },
  {
    label: 'Save Favorites',
    description: 'Save your favorite properties and get notified about price changes and status updates.',
    icon: <FavoriteIcon sx={{ fontSize: 80, color: '#1B5E20' }} />,
    color: '#e8f5e9'
  },
  {
    label: 'Real-time Alerts',
    description: 'Set up custom alerts and get notified instantly when new properties match your criteria.',
    icon: <NotificationsActiveIcon sx={{ fontSize: 80, color: '#1B5E20' }} />,
    color: '#f1f8e9'
  }
];

const OnboardingCards = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasRole } = useRole();
  const [activeStep, setActiveStep] = useState(0);
  const maxSteps = onboardingSteps.length;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Determine destination based on user role
  const getDestination = () => {
    if (hasRole(ROLES.ADMIN)) {
      return '/dashboard';
    } else {
      return '/nj-counties';
    }
  };

  const handleNext = () => {
    if (activeStep === maxSteps - 1) {
      // If it's the last step, navigate based on role
      navigate(getDestination(), { replace: true });
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepChange = (step) => {
    setActiveStep(step);
  };

  const handleSkip = () => {
    navigate(getDestination(), { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f0fdf4',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            backgroundColor: 'white',
            position: 'relative'
          }}
        >
          <AutoPlaySwipeableViews
            axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
            index={activeStep}
            onChangeIndex={handleStepChange}
            enableMouseEvents
            interval={6000}
          >
            {onboardingSteps.map((step, index) => (
              <div key={step.label}>
                {Math.abs(activeStep - index) <= 2 ? (
                  <Box
                    sx={{
                      minHeight: isMobile ? '60vh' : '50vh',
                      p: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: step.color,
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    <Box
                      sx={{
                        mb: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(27, 94, 32, 0.1)'
                      }}
                    >
                      {step.icon}
                    </Box>
                    <Typography
                      variant="h4"
                      component="h2"
                      align="center"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        color: '#1B5E20',
                        mb: 2
                      }}
                    >
                      {step.label}
                    </Typography>
                    <Typography
                      variant="body1"
                      align="center"
                      sx={{
                        color: '#2e7d32',
                        maxWidth: '400px',
                        mx: 'auto'
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                ) : null}
              </div>
            ))}
          </AutoPlaySwipeableViews>

          <MobileStepper
            steps={maxSteps}
            position="static"
            activeStep={activeStep}
            sx={{
              backgroundColor: 'transparent',
              '& .MuiMobileStepper-dot': {
                backgroundColor: '#a5d6a7'
              },
              '& .MuiMobileStepper-dotActive': {
                backgroundColor: '#1B5E20'
              }
            }}
            nextButton={
              <Button
                size="small"
                onClick={handleNext}
                sx={{
                  color: '#1B5E20',
                  '&:hover': {
                    backgroundColor: 'rgba(27, 94, 32, 0.1)'
                  }
                }}
              >
                {activeStep === maxSteps - 1 ? 'Get Started' : 'Next'}
                {theme.direction === 'rtl' ? (
                  <KeyboardArrowLeft />
                ) : (
                  <KeyboardArrowRight />
                )}
              </Button>
            }
            backButton={
              <Button
                size="small"
                onClick={handleBack}
                disabled={activeStep === 0}
                sx={{
                  color: '#1B5E20',
                  '&:hover': {
                    backgroundColor: 'rgba(27, 94, 32, 0.1)'
                  }
                }}
              >
                {theme.direction === 'rtl' ? (
                  <KeyboardArrowRight />
                ) : (
                  <KeyboardArrowLeft />
                )}
                Back
              </Button>
            }
          />
        </Paper>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            onClick={handleSkip}
            sx={{
              color: '#1B5E20',
              textDecoration: 'underline',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline'
              }
            }}
          >
            Skip Onboarding
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default OnboardingCards; 